import {observer} from 'mobx-react-lite'
import React, {useEffect, useRef, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Util} from 'yougile-util'
import {TextArea} from '@components/common/base/textarea'
import {TextAreaApi} from '@components/common/base/textarea/types'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {useForceUpdate} from '@containers/hooks'
import {EmailItem} from './email-item'
import {getStatusContainer, reorder} from './helpers'

const TwContainer = tw.div<{$error?: ValidType; $size: 'medium' | 'small'}>`
  rounded-8
  border-2
  border-solid
  bg-background-primary

  pl-12
  py-8

  flex
  flex-wrap
  gap-8
  items-start

  ${p => {
    const cx = []

    if (p.$size === 'medium') {
      cx.push('min-h-80')
    }

    if (p.$error === 'error') {
      cx.push('border-status-error')
    }

    if (p.$error === 'warning') {
      cx.push('border-status-warning')
    }

    if (p.$error === 'success') {
      cx.push('border-action-default')
    }

    return cx.join(' ')
  }}
`

export type ValidType = 'success' | 'warning' | 'error'

export interface EmailsInputItem {
  text: string
  valid?: ValidType
}

export interface Props {
  onChange?: (emails: EmailsInputItem[]) => void
  size: 'small' | 'medium'
  validate?: (text: string) => ValidType
  enter?: () => void
  esc?: () => void
  className?: string
}

const spaceReg = /\s|,/gi

export const EmailsInput = observer((props: Props) => {
  const textAreaRef = useRef<HTMLTextAreaElement>()
  const containerRef = useRef<HTMLElement>()
  const forceUpdate = useForceUpdate()
  const refSetTextAreaValue = useRef<(value?: string) => void>()

  const {emails, processText, removeItem} = useEmailsState(props)

  const onProcessText = (api: TextAreaApi) => {
    processText(api.text)
    api.resetValue?.()
    refSetTextAreaValue.current = api.resetValue

    setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.scrollTop = containerRef.current.scrollHeight
        forceUpdate()
      }
    }, 100)
  }

  const onSubmit = (api: TextAreaApi) => {
    onProcessText(api)
    props.enter?.()
  }

  const onChange = (api: TextAreaApi) => {
    const text = api.text
    const lastChar = text[text.length - 1]
    if (!spaceReg.test(lastChar)) {
      return
    }

    processText(api.text)
    api.resetValue?.()
    refSetTextAreaValue.current = api.resetValue
  }

  const onCancel = (api: TextAreaApi) => {
    api.resetValue?.()
    props.esc?.()
  }

  const onContainerClick = () => {
    textAreaRef.current?.focus()
  }

  useEffect(() => {
    if (!textAreaRef.current) {
      return undefined
    }

    const listener = (ev: KeyboardEvent) => {
      if (
        ev.key === 'Backspace' &&
        textAreaRef.current?.value === '' &&
        emails.length > 0
      ) {
        ev.preventDefault()
        ev.stopPropagation()
        const lastEmail = emails[emails.length - 1]
        removeItem(lastEmail.text)
        refSetTextAreaValue.current?.(lastEmail.text)
        setTimeout(() => {
          textAreaRef.current?.setSelectionRange(0, lastEmail.text.length)
        }, 100)
      }
    }

    textAreaRef.current.addEventListener('keydown', listener)

    return () => {
      textAreaRef.current?.removeEventListener('keydown', listener)
    }
  }, [textAreaRef.current, emails, refSetTextAreaValue.current])

  const placeholder = emails.length
    ? undefined
    : Lang.msg.invitePopup.inputPlaceholderLong

  const containerStatus = getStatusContainer(emails)

  return (
    <TwContainer
      $size={props.size}
      onClick={onContainerClick}
      $error={containerStatus}
    >
      <WithPerfectScrollbar
        className="min-h-30 max-h-300 w-full  flex  flex-wrap  gap-8  items-start pr-12"
        containerRef={el => {
          if (el) {
            containerRef.current = el
          }
        }}
      >
        <>
          {emails.map(item => {
            const onClose = () => {
              removeItem(item.text)
            }
            return (
              <EmailItem
                key={item.text}
                text={item.text}
                valid={item.valid}
                close={onClose}
              />
            )
          })}

          <TextArea
            className="flex-grow"
            onInit={el => {
              if (el) {
                textAreaRef.current = el
              }
            }}
            onSubmit={onSubmit}
            onChange={onChange}
            onCancel={onCancel}
            onBlur={onProcessText}
            onPaste={onProcessText}
            autoFocus
            borders={false}
            paddings={'none'}
            style={{lineHeight: '26px'}}
            noBackground
            defaultValue={placeholder ? '' : ' '}
            placeholder={placeholder}
          />
        </>
      </WithPerfectScrollbar>
    </TwContainer>
  )
})

const useEmailsState = (props: Props) => {
  const [emails, setEmails] = useState<EmailsInputItem[]>([])

  const validate = (text: string) => {
    if (!Util.isEmail(text)) {
      return 'error'
    }

    if (!props.validate) {
      return 'success'
    }

    return props.validate(text)
  }

  const processText = (input: string) => {
    const texts = input.split(spaceReg)

    const newItems = [...emails]

    for (const text of texts) {
      if (!text || newItems.find(i => i.text === text)) {
        continue
      }

      newItems.push({
        text: Util.cleanEmail(text),
        valid: validate(text)
      })
    }

    reorder(newItems)

    setEmails(newItems)
    props.onChange?.(newItems)
  }

  const removeItem = (text: string) => {
    const newItems = emails.filter(i => i.text !== text)

    reorder(newItems)

    setEmails(newItems)
    props.onChange?.(newItems)
  }

  return {emails, processText, removeItem}
}
